import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CohortFunnel as Funnel } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { ErrorNote, Loading, Panel } from '../ui'

/**
 * A cohort's funnel: how many reached each step, how many are at it now, and
 * how many of those are stuck (waiting longer than normal) or blocked (waiting
 * on us). Kit and Prescription run side by side after purchase; the grid shows
 * where people stand on both at once. Pressing any number lists the people.
 */

type Person = Funnel['people'][number]
type Pick =
  | { kind: 'status'; step: string; status: string; label: string }
  | { kind: 'here'; step: string; label: string }
  | { kind: 'cell'; kit: string; prescription: string; label: string }
  | { kind: 'exit'; exit: 'refunded' | 'not_eligible'; label: string }

const DAY = 86_400_000
const daysSince = (iso: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY) : null)

export function CohortFunnel({ id, onOpenPerson }: { id: string; onOpenPerson: (id: string) => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.analytics.cohortFunnel.queryOptions({ bucketId: id }))
  const [pick, setPick] = useState<Pick | null>(null)

  if (q.error) return <Panel title="Funnel"><ErrorNote error={q.error} /></Panel>
  if (!q.data) return <Panel title="Funnel"><Loading what="the funnel" /></Panel>
  const f = q.data
  const step = (sid: string) => f.steps.find((s) => s.id === sid)!
  const max = Math.max(1, f.inFunnel)

  /* A plain call, not an inline component: an inline one remounts every render and forgets it was open. */
  const stepEl = (sid: string) => <Step key={sid} s={step(sid)} max={max} onPick={setPick} />

  const cell = (kit: string, rx: string) => f.grid.cells.find((c) => c.kit === kit && c.prescription === rx)?.count ?? 0

  return (
    <Panel title="Funnel" note={`${f.inFunnel} in the funnel · press a number to see who`}>
      {stepEl('purchased')}
      <div className="fn-tracks">
        <div>
          <h4 className="fn-track">Kit</h4>
          {stepEl('shipped')}
          {stepEl('delivered')}
        </div>
        <div>
          <h4 className="fn-track">Prescription</h4>
          {stepEl('asked')}
          {stepEl('sent')}
          {stepEl('read')}
          {stepEl('eligible')}
        </div>
      </div>

      <h4 className="fn-track">Kit and prescription together</h4>
      <div className="scroll-x">
        <table className="data fn-grid">
          <thead>
            <tr>
              <th>Kit ↓ · Prescription →</th>
              {f.grid.prescription.map((p) => (
                <th key={p.id} className="r">{p.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {f.grid.kit.map((k) => (
              <tr key={k.id} style={{ cursor: 'default' }}>
                <td>{k.label}</td>
                {f.grid.prescription.map((p) => {
                  const n = cell(k.id, p.id)
                  const ready = k.id === 'delivered' && p.id === 'eligible'
                  return (
                    <td key={p.id} className={`r num${ready ? ' fn-ready' : ''}`}>
                      {n > 0 ? (
                        <button
                          type="button"
                          className="linklike"
                          onClick={() => setPick({ kind: 'cell', kit: k.id, prescription: p.id, label: `${k.label} · ${p.label}` })}
                        >
                          {n}
                        </button>
                      ) : (
                        <span className="muted">0</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="footnote">Bottom right, delivered and eligible: ready to activate.</p>

      <h4 className="fn-track">Training</h4>
      {stepEl('activated')}
      {stepEl('started')}
      {stepEl('onboarded')}

      <p className="fn-exits">
        Left the funnel:{' '}
        <button type="button" className="linklike" onClick={() => setPick({ kind: 'exit', exit: 'refunded', label: 'Refunded' })}>
          refunded {f.exits.refunded}
        </button>{' '}
        ·{' '}
        <button type="button" className="linklike" onClick={() => setPick({ kind: 'exit', exit: 'not_eligible', label: 'Not eligible' })}>
          not eligible {f.exits.notEligible}
        </button>
        {f.exits.refundFailed > 0 && <span className="fn-tag bad"> {f.exits.refundFailed} refund failed</span>}
      </p>

      {pick && <PeopleList f={f} pick={pick} onClose={() => setPick(null)} onOpenPerson={onOpenPerson} />}
    </Panel>
  )
}

/* Its own component, so a step stays open when the list below it changes. */
function Step({ s, max, onPick }: { s: Funnel['steps'][number]; max: number; onPick: (p: Pick) => void }) {
    const [open, setOpen] = useState(false)
    return (
      <div className="fn-step">
        <button type="button" className="fn-head" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="fn-label">{s.label}</span>
          <span className="fn-bar" aria-hidden="true">
            <span style={{ width: `${(s.reached / max) * 100}%` }} />
          </span>
          <span className="fn-num num">{s.reached}</span>
        </button>
        <div className="fn-now">
          {s.here > 0 ? (
            <button type="button" className="linklike" onClick={() => onPick({ kind: 'here', step: s.id, label: `At "${s.label}" now` })}>
              {s.here} here now
            </button>
          ) : (
            <span className="muted">none here now</span>
          )}
          {s.stuck > 0 && <span className="fn-tag warn"> {s.stuck} stuck</span>}
          {s.blocked > 0 && <span className="fn-tag bad"> {s.blocked} waiting on us</span>}
        </div>
        {open && (
          <ul className="fn-statuses">
            {s.statuses.map((st) => (
              <li key={st.status}>
                <button
                  type="button"
                  className="linklike"
                  onClick={() => onPick({ kind: 'status', step: s.id, status: st.status, label: `${s.label}: ${st.label}` })}
                >
                  {st.label}
                </button>{' '}
                <span className="num">{st.count}</span>
                {st.stuck > 0 && <span className="fn-tag warn"> {st.stuck} stuck</span>}
                {st.blocked > 0 && <span className="fn-tag bad"> waiting on us</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
}

function PeopleList({ f, pick, onClose, onOpenPerson }: { f: Funnel; pick: Pick; onClose: () => void; onOpenPerson: (id: string) => void }) {
  const firstUndone = (p: Person, track: string) =>
    f.steps.find((s) => s.track === track && !p.steps[s.id]!.done)?.id
  const matches = (p: Person) => {
    if (pick.kind === 'exit') return p.exit === pick.exit
    if (p.exit) return false
    if (pick.kind === 'status') return p.steps[pick.step]?.status === pick.status
    if (pick.kind === 'cell') return p.grid?.kit === pick.kit && p.grid.prescription === pick.prescription
    const track = f.steps.find((s) => s.id === pick.step)!.track
    if (track === 'app' && (firstUndone(p, 'kit') || firstUndone(p, 'prescription'))) return false
    return firstUndone(p, track) === pick.step
  }
  /* The step whose status to show beside each name. */
  const shown = (p: Person) =>
    pick.kind === 'status' || pick.kind === 'here'
      ? p.steps[pick.step]!
      : p.steps[firstUndone(p, 'prescription') ?? firstUndone(p, 'kit') ?? 'onboarded']!
  const list = f.people
    .filter(matches)
    .map((p) => ({ p, s: shown(p) }))
    .sort((a, b) => (daysSince(b.s.since) ?? -1) - (daysSince(a.s.since) ?? -1))

  return (
    <div className="fn-people">
      <div className="fn-people-h">
        <b>{pick.label}</b> <span className="muted">· {list.length}, longest waiting first</span>
        <button type="button" className="btn ghost" onClick={onClose}>Close</button>
      </div>
      <table className="data">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th className="r">Days</th>
            <th>Kit</th>
            <th>Prescription</th>
          </tr>
        </thead>
        <tbody>
          {list.map(({ p, s }) => (
            <tr key={p.id} onClick={() => onOpenPerson(p.id)}>
              <td>
                {p.name}
                {p.email && <div className="muted">{p.email}</div>}
              </td>
              <td>
                {s.label}
                {s.stuck && <span className="fn-tag warn"> stuck</span>}
                {s.blocked && <span className="fn-tag bad"> waiting on us</span>}
              </td>
              <td className="r num">{daysSince(s.since) ?? '—'}</td>
              <td className="muted">{p.steps.delivered!.done ? 'Delivered' : p.steps.delivered!.status === 'not_yet' ? p.steps.shipped!.label : p.steps.delivered!.label}</td>
              <td className="muted">{(p.steps[firstUndone(p, 'prescription') ?? 'eligible'] ?? p.steps.eligible)!.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
